import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export function EmailVerification() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setVerificationStatus('error');
        setErrorMessage('No verification token provided');
        return;
      }

      try {
        // First, check if token is valid before attempting verification
        const checkResponse = await axios.get(`${BASE_URL}/auth/check-token/${token}`);
        
        if (!checkResponse.data.valid) {
          throw new Error('Invalid or expired verification token');
        }

        // Proceed with verification
        const response = await axios.post(`${BASE_URL}/auth/verify-email`, { token });
        
        if (response.data.success) {
          setVerificationStatus('success');
          // Store verification status
          localStorage.setItem('emailVerified', 'true');
          
          // Show success toast
          toast.success('Email verified successfully!', {
            description: 'You can now log in to your business account.',
            duration: 5000,
          });
          
          // Set redirecting state
          setIsRedirecting(true);
          
          // Redirect after a delay to show the success message
          setTimeout(() => {
            navigate('/login?verified=true');
          }, 3000);
        } else {
          throw new Error(response.data.error || 'Verification failed');
        }
      } catch (error: any) {
        console.error('Verification error:', error);
        setVerificationStatus('error');
        setErrorMessage(
          error.response?.data?.error || 
          error.message || 
          'Failed to verify email. Please try again.'
        );
        
        // Show error toast with more specific message
        toast.error('Verification Failed', {
          description: error.response?.data?.error || 'Please try again or contact support.',
          duration: 7000,
        });
      }
    };

    verifyEmail();
  }, [token, navigate]);

  const handleResendVerification = async () => {
    const email = localStorage.getItem('pendingVerificationEmail');
    
    if (!email) {
      toast.error('Email not found', {
        description: 'Please try registering again or contact support.',
      });
      return;
    }

    try {
      const response = await axios.post(`${BASE_URL}/auth/resend-verification`, { email });
      
      if (response.data.success) {
        toast.success('Verification Email Sent!', {
          description: 'Please check your inbox for the new verification link.',
        });
      } else {
        throw new Error(response.data.error || 'Failed to resend verification email');
      }
    } catch (error: any) {
      console.error('Resend verification error:', error);
      toast.error('Failed to Resend Email', {
        description: error.response?.data?.error || 'Please try again or contact support.',
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg"
      >
        {verificationStatus === 'pending' && (
          <div className="text-center">
            <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto" />
            <h2 className="mt-6 text-xl font-bold text-gray-900">
              Verifying Your Email...
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we verify your email address.
            </p>
          </div>
        )}

        {verificationStatus === 'success' && (
          <div className="text-center">
            <motion.div 
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100"
            >
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </motion.div>
            <h2 className="mt-6 text-xl font-bold text-gray-900">
              Email Verified Successfully! 🎉
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your email has been verified. You can now log in to your business account.
            </p>
            <div className="mt-6">
              {isRedirecting ? (
                <div className="text-sm text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                  Redirecting to login...
                </div>
              ) : (
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-primary hover:bg-[#234824]"
                >
                  Go to Login
                </Button>
              )}
            </div>
          </div>
        )}

        {verificationStatus === 'error' && (
          <div className="text-center">
            <motion.div 
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100"
            >
              <AlertCircle className="h-6 w-6 text-red-600" />
            </motion.div>
            <h2 className="mt-6 text-xl font-bold text-gray-900">
              Verification Failed
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {errorMessage}
            </p>
            <div className="mt-6 space-y-3">
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full"
              >
                Try Again
              </Button>
              <Button
                onClick={handleResendVerification}
                variant="outline"
                className="w-full"
              >
                Resend Verification Email
              </Button>
              <Button
                onClick={() => navigate('/contact-support')}
                variant="link"
                className="w-full text-primary"
              >
                Contact Support
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
} 